import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, SlotInfo, EventProps, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import { DatabaseEvent as MyEvent } from '../../../types/types';
import EventForm from '../eventForm/index';
import { useEventTags } from '../../../hooks/useEventTags';
import { fetchEvents, deleteEvent, updateEvent } from '../../../services/eventService';
import EventDetails from '../eventDetails/index';
import CalendarNoteEditor from '../calendarNotes';
import MajorEventCountdown from '../majorEventCountdown';
import useUserContext from '../../../hooks/useUserContext';
import { NoteType } from '../../../hooks/useCalendarNote';
import './index.css';

/**
 * Localizer setup for react-big-calendar using moment.js
 */
const localizer = momentLocalizer(moment);

/**
 * CalendarPage component
 * This component displays a calendar with events,
 * and allows users to create, edit, and delete events.
 *
 * @returns CalendarPage component
 */
const CalendarPage = () => {
  const { eventTags, selectedTag, setSelectedTag, filterEventsByTag } = useEventTags();
  const { user: currentUser } = useUserContext();

  const [events, setEvents] = useState<MyEvent[]>([]);
  const [nextMajorEvent, setNextMajorEvent] = useState<MyEvent | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<MyEvent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');

  const [currentView, setCurrentView] = useState<View>('month'); // default
  const [viewDate, setViewDate] = useState<Date>(new Date());

  const [, setError] = useState<string>('');

  /**
   * getNoteTypeFromView function
   * This function determines the note type based on the current view of the calendar.
   *
   * @param view - The current view of the calendar (month, week, day)
   * @returns The note type based on the current view
   */
  const getNoteTypeFromView = (view: View): NoteType => {
    if (view === 'month') return 'month';
    if (view === 'week') return 'week';
    return 'day';
  };

  /**
   * handleCalendarChange function
   * This function handles the change of the calendar view and date.
   *
   * @param date - The new date to set the calendar to
   * @param view - The new view to set the calendar to
   */
  const handleCalendarChange = (date: Date, view: View) => {
    setCurrentView(view);
    setViewDate(date);
  };

  /**
   * filterEventsByTag function
   * This function filters events based on the selected tag.
   *
   * @param events - The list of events to filter
   * @returns The filtered list of events
   */
  // Filter events by tag
  const filteredEvents = filterEventsByTag(events).filter(event => {
    if (visibilityFilter === 'all') {
      return (
        event.visibility === 'public' ||
        event.userCreated === currentUser.username ||
        event.userParticipating?.includes(currentUser.username)
      );
    }

    if (visibilityFilter === 'public') return event.visibility === 'public';

    if (visibilityFilter === 'private') {
      return (
        event.visibility === 'private' &&
        (event.userCreated === currentUser.username ||
          event.userParticipating?.includes(currentUser.username))
      );
    }
    return false;
  });

  /**
   * loadEvents function
   * This function loads events from the server and sets them in the state.
   */
  const loadEvents = async () => {
    try {
      const data = await fetchEvents(currentUser.username);
      if (Array.isArray(data)) {
        const parsedEvents = data.map(event => ({
          ...event,
          timeStart: new Date(event.timeStart),
          timeEnd: new Date(event.timeEnd),
        }));
        setEvents(parsedEvents);

        const majorEvents = parsedEvents
          .filter(e => e.isMajor && new Date(e.timeStart) > new Date())
          .sort((a, b) => new Date(a.timeStart).getTime() - new Date(b.timeStart).getTime());
        setNextMajorEvent(majorEvents[0] || null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Unexpected error loading events');
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * handleSelectEvent function
   * This function handles the selection of an event in the calendar.
   *
   * @param event - The selected event
   */
  // Handle event selection
  const handleSelectEvent = (event: unknown) => {
    setSelectedEvent(event as MyEvent);
    setIsDetailsOpen(true);
    setIsFormOpen(false);
    setIsEditing(false);
  };

  /**
   * handleSelectSlot function
   * This function handles the selection of a slot in the calendar.
   *
   * @param slotInfo - The slot information of the selected slot
   */
  // Handle slot selection
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setSelectedDate(slotInfo.start);
    setSelectedEvent(null);
    setIsFormOpen(true);
    setIsEditing(false);
  };

  /**
   * handleCloseForm function
   * This function handles the closing of the event form.
   */
  // Close event form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setIsEditing(false);
    setSelectedDate(null);
  };

  /**
   * handleEventSave function
   * This function handles the saving of an event.
   *
   * @param updatedEvent - The updated event data
   */
  // Save event
  const handleEventSave = async (updatedEvent: Partial<MyEvent>) => {
    try {
      if (isEditing && selectedEvent) {
        await updateEvent(updatedEvent._id!.toString(), updatedEvent);
      }
      await loadEvents();
      handleCloseForm();
    } catch (err) {
      setError('Failed to save event');
    }
  };

  /** Closes the event details modal. */
  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedEvent(null);
  };

  /** Enables editing mode for a selected event. */
  const handleEditEvent = () => {
    setIsFormOpen(true);
    setIsDetailsOpen(false);
    setIsEditing(true);
  };

  /** Deletes a selected event from the backend. */
  const handleDeleteEvent = async () => {
    if (!selectedEvent?._id) return;

    try {
      await deleteEvent(selectedEvent._id.toString());
      setEvents(prevEvents =>
        prevEvents.filter(event => event._id.toString() !== selectedEvent._id.toString()),
      );
      handleCloseDetails();
    } catch (err) {
      setError('Failed to delete event');
    }
  };

  /**
   * eventStyleGetter function
   * This function styles the events in the calendar based on their tags and properties.
   *
   * @param event - The event to style
   * @returns The style object for the event
   */
  const eventStyleGetter = (event: MyEvent): { style: React.CSSProperties } => {
    const tagData = eventTags.find(tag => tag.name === event.tag);
    const tagColor = tagData ? tagData.color : '#2d7df6';

    const baseStyle: React.CSSProperties = {
      backgroundColor: tagColor,
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: 'none',
      display: 'block',
      padding: '5px',
    };

    if (event.isMajor) {
      return {
        style: {
          ...baseStyle,
          border: '2px solid rgb(0, 179, 255)',
          boxShadow: '0 0 10px rgb(0, 179, 255)',
          fontWeight: 'bold',
        },
      };
    }

    return { style: baseStyle };
  };

  /**
   * CustomEvent function
   * This function renders a custom event component in the calendar.
   *
   * @param param0 - The event to render
   * @returns The custom event component
   */
  const CustomEvent = ({ event }: EventProps<MyEvent>) => (
    <div>
      {event.isMajor && '⭐ '}
      {event.eventTitle}
    </div>
  );

  return (
    <div className='calendar-container'>
      <div className='calendar-toolbar'>
        <div className='filters'>
          {/* Filtering events by tag */}
          <div className='tag-selection'>
            <label>Event types: </label>
            <select onChange={e => setSelectedTag(e.target.value)} value={selectedTag || ''}>
              <option value=''>All</option>
              {eventTags.map(tag => (
                <option key={tag.name} value={tag.name}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>

          <div className='visibility-selection'>
            <label>Visibility: </label>
            <select
              value={visibilityFilter}
              onChange={e => setVisibilityFilter(e.target.value as 'all' | 'public' | 'private')}>
              <option value='all'>All</option>
              <option value='public'>Public</option>
              <option value='private'>Private</option>
            </select>
          </div>
        </div>

        {nextMajorEvent && (
          <div style={{ float: 'right', marginBottom: '10px' }}>
            <MajorEventCountdown
              eventTitle={nextMajorEvent.eventTitle}
              timeStart={new Date(nextMajorEvent.timeStart)}
            />
          </div>
        )}
      </div>

      <Calendar<MyEvent>
        localizer={localizer}
        events={filteredEvents}
        startAccessor='timeStart'
        endAccessor='timeEnd'
        style={{ height: 500 }}
        selectable
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        eventPropGetter={eventStyleGetter}
        components={{
          event: CustomEvent,
        }}
        views={['month', 'week', 'day']}
        view={currentView}
        onNavigate={date => handleCalendarChange(date, currentView)}
        onView={view => handleCalendarChange(viewDate, view)}
      />

      <div className='tag-key'>
        {/* Event Tags Header */}
        <h3 className='event-tags-header'>Event Key</h3>

        {/* Color Key */}
        <div className='color-key'>
          {eventTags.map(tag => (
            <div key={tag.name} className='color-key-item' style={{ backgroundColor: tag.color }}>
              {tag.name}
            </div>
          ))}
        </div>
      </div>

      <div className='calendar-bottom-section'>
        <div className='calendar-left-panel'>
          {isFormOpen && (
            <EventForm
              date={selectedDate || new Date()}
              onClose={handleCloseForm}
              onEventCreate={handleEventSave}
              event={isEditing && selectedEvent ? selectedEvent : undefined}
            />
          )}

          {isDetailsOpen && selectedEvent && (
            <EventDetails
              event={selectedEvent}
              onClose={handleCloseDetails}
              onEdit={handleEditEvent}
              onDelete={handleDeleteEvent}
            />
          )}
        </div>

        <div className='calendar-right-panel'>
          <CalendarNoteEditor
            type={getNoteTypeFromView(currentView)}
            date={viewDate}
            user={currentUser.username}
          />
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
